/**
 * v1.14.51 — Admin Social Carousel Generator (FASE A MVP)
 * Wizard 4 step: topic → slide selection → AI draft + edit → branding & export.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sparkles, Loader2, Download, Trash2, Copy, RefreshCw,
  CheckCircle2, AlertTriangle, FileDown, History, Upload, Image as ImageIcon,
  Globe, ZapOff, Star,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axc = { withCredentials: true };

const STEP_LABELS = ["Topic", "Slide Mix", "AI Draft & Edit", "Branding & Export"];

const PRESET_OPTIONS = [
  { id: "editorial", label: "Editorial Geopop (default)",
    desc: "Layout editoriale Geopop × Starting Finance." },
  { id: "insurance_editorial_meme", label: "Insurance Editorial Meme",
    desc: "Meme editoriale 8 slide stile Starting Finance × News." },
];

const MEME_AUDIENCES = ["clienti", "aziende", "broker", "agenti",
  "professionisti", "famiglie"];
const MEME_BRANCHES = ["auto", "casa", "salute", "vita", "cyber", "azienda",
  "professionale", "viaggio", "generale"];
const MEME_TONES = ["educational", "provocative", "premium", "ironic"];
const MEME_CTA_TYPES = ["podcast", "report", "consulenza", "newsletter",
  "guida", "preventivo"];

const MEME_TEMPLATE_ORDER = [
  "meme_past", "meme_present", "meme_news", "meme_bignumber",
  "meme_reasons", "meme_risks", "meme_impact", "meme_cta",
];

const FRESHNESS_MODES = [
  { value: "auto", label: "Auto (HARD con fallback)", icon: Sparkles, desc: "Tenta web research; se 0 fonti → soft" },
  { value: "hard", label: "HARD (LLM research +24h cache)", icon: Globe, desc: "Research step Gemini su fonti italiane recenti" },
  { value: "soft", label: "SOFT (solo prompt)", icon: ZapOff, desc: "Niente research, solo system prompt" },
];

const SLIDE_PLACEHOLDER = {
  // v1.14.55 — meme templates (insurance_editorial_meme preset)
  meme_past: { counter_text: "1/8", headline: "", body: "", asset_suggestions: ["documento","soldi","ombrello"], headline_highlight_parts: [] },
  meme_present: { counter_text: "2/8", headline: "", asset_suggestions: ["scudo"], headline_highlight_parts: [] },
  meme_news: { counter_text: "3/8", headline: "", news_card_titles: ["", "", ""], headline_highlight_parts: [] },
  meme_bignumber: { counter_text: "4/8", headline: "", body: "", metric_main: "", metric_unit: "", metric_kind: "neutral", keywords_bold: [], asset_suggestions: ["grafico_up"], headline_highlight_parts: [] },
  meme_reasons: { counter_text: "5/8", headline: "", reasons_blocks: [{icon:"documento",text:""},{icon:"soldi",text:""},{icon:"warning",text:""}], headline_highlight_parts: [] },
  meme_risks: { counter_text: "6/8", headline: "", risks_blocks: [{icon:"cyber",title:"",subtitle:""},{icon:"alluvione",title:"",subtitle:""}], headline_highlight_parts: [] },
  meme_impact: { counter_text: "7/8", headline: "", info_box_title: "", info_box_eyebrow: "IN PRATICA", info_box_border: "purple", body: "", asset_suggestions: ["soldi"], headline_highlight_parts: [] },
  meme_cta: { counter_text: "8/8", headline: "", cta: {text:"", thumb_label:"", type:"podcast"}, asset_suggestions: ["telefono"], headline_highlight_parts: [] },
  // v1.14.53 — editorial
  cover: { eyebrow: "EDUCATIVO · ASSICURAZIONI", headline: "", body: "", metric_main: "", metric_unit: "", source_label: "", visual_hint: "lightbulb", metric_kind: "neutral" },
  context: { eyebrow: "IL CONTESTO", headline: "", body: "", keywords_highlight: [], visual_hint: "lightbulb", source_label: "" },
  educational: { eyebrow: "COSA SIGNIFICA", headline: "", body: "", keywords_highlight: [], metric_main: "", metric_unit: "", bars: [], source_label: "" },
  summary: { eyebrow: "LA COSA IMPORTANTE DA RICORDARE", headline: "", points: ["", "", ""] },
  cta_v2: { eyebrow: "SALVA QUESTO POST", headline: "Hai bisogno di una consulenza?", body: "Salva questo post per averlo a portata. Chiamaci senza impegno.", phone: "081 761 1000", source_label: "IVASS A000464752" },
  bignum_hero: { eyebrow: "IL DATO", metric_main: "", metric_unit: "", headline: "", source_label: "", metric_kind: "neutral" },
  quote_ceo: { eyebrow: "IL PUNTO DI VISTA", quote_text: "", quote_author: "Christian Colucci", quote_role: "CEO · IVASS A000464752" },
  comparison: { eyebrow: "FACCIAMO UN ESEMPIO", headline: "", comparison: { left_title: "Senza polizza", left_points: ["", "", ""], right_title: "Con polizza Colucci", right_points: ["", "", ""] } },
  mappa: { eyebrow: "DOVE SUCCEDE", headline: "", body: "", keywords_highlight: [], mappa_regioni: [], source_label: "" },
  // legacy v1.14.51 (per backward-compat)
  hook: { tag: "INSURANCE · ALERT", title_top: "", title_italic: "", title_bottom: "", metric: "", metric_label: "", fonte: "", metric_kind: "negative" },
  barchart: { title: "", bars: [{ label: "2020", value: 0 }, { label: "2021", value: 0 }, { label: "2022", value: 0 }, { label: "2023", value: 0 }, { label: "2024", value: 0 }], fonte: "" },
  kpi3: { title: "", kpis: [{ value: "", label: "", delta: "", delta_kind: "positive" }, { value: "", label: "", delta: "", delta_kind: "neutral" }, { value: "", label: "", delta: "", delta_kind: "warn" }], fonte: "" },
  warning: { tag: "WARNING", title: "", points: ["", "", ""] },
  checklist: { tag: "Checklist", title: "", points: ["", "", "", "", ""], disclaimer: "" },
  quote: { citation: "", author: "Christian Colucci", qualifica: "CEO · IVASS A000464752 · Colucci Assicuratori" },
  cta: { title: "Hai bisogno di una consulenza?", subtitle: "", phone: "081 761 1000", ivass_tag: "IVASS A000464752", location: "Napoli · Portici · Boscoreale · 1985 → 2025" },
};

function StepBadge({ index, label, active, done }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold border ${active ? "bg-emerald-50 border-emerald-300 text-emerald-800" : done ? "bg-zinc-50 border-zinc-200 text-zinc-700" : "bg-white border-zinc-200 text-zinc-400"}`}>
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${active ? "bg-emerald-600 text-white" : done ? "bg-zinc-700 text-white" : "bg-zinc-200 text-zinc-500"}`}>{done ? "✓" : index + 1}</span>
      <span>{label}</span>
    </div>
  );
}

// =====================================================================
// SLIDE EDITORS (one per type)
// =====================================================================
function HookEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <FieldText label="Tag (badge)" value={data.tag} onChange={(v) => set("tag", v)} testid="hook-tag" />
      <FieldText label="Metric Kind" value={data.metric_kind || "negative"} onChange={(v) => set("metric_kind", v)} placeholder="negative/positive/neutral" testid="hook-metric-kind" />
      <FieldText label="Titolo riga 1" value={data.title_top} onChange={(v) => set("title_top", v)} testid="hook-title-top" />
      <FieldText label="Titolo in corsivo" value={data.title_italic} onChange={(v) => set("title_italic", v)} testid="hook-title-italic" />
      <FieldText label="Titolo riga 3" value={data.title_bottom} onChange={(v) => set("title_bottom", v)} testid="hook-title-bottom" />
      <FieldText label="Metrica (grande)" value={data.metric} onChange={(v) => set("metric", v)} placeholder="+18% o 14.500" testid="hook-metric" />
      <FieldText label="Etichetta metrica" value={data.metric_label} onChange={(v) => set("metric_label", v)} testid="hook-metric-label" />
      <FieldText label="Fonte" value={data.fonte} onChange={(v) => set("fonte", v)} testid="hook-fonte" />
    </div>
  );
}

function BarchartEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const updateBar = (i, k, v) => {
    const bars = [...(data.bars || [])];
    bars[i] = { ...bars[i], [k]: k === "value" ? parseFloat(v) || 0 : v };
    set("bars", bars);
  };
  return (
    <div className="grid gap-3">
      <FieldText label="Titolo" value={data.title} onChange={(v) => set("title", v)} testid="bar-title" />
      <FieldText label="Fonte" value={data.fonte} onChange={(v) => set("fonte", v)} testid="bar-fonte" />
      <div className="grid gap-2">
        {(data.bars || []).map((b, i) => (
          <div key={i} className="grid grid-cols-3 gap-2" data-testid={`bar-row-${i}`}>
            <Input value={b.label} onChange={(e) => updateBar(i, "label", e.target.value)} placeholder="Etichetta" data-testid={`bar-${i}-label`} />
            <Input value={b.value} onChange={(e) => updateBar(i, "value", e.target.value)} placeholder="Valore" type="number" data-testid={`bar-${i}-value`} />
            <Input value={b.value_display || ""} onChange={(e) => updateBar(i, "value_display", e.target.value)} placeholder="Display (es. €1.2 mld)" data-testid={`bar-${i}-display`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi3Editor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const updateKpi = (i, k, v) => {
    const kpis = [...(data.kpis || [])];
    kpis[i] = { ...kpis[i], [k]: v };
    set("kpis", kpis);
  };
  return (
    <div className="grid gap-3">
      <FieldText label="Titolo" value={data.title} onChange={(v) => set("title", v)} testid="kpi-title" />
      {(data.kpis || []).map((k, i) => (
        <div key={i} className="border rounded-lg p-3 grid gap-2" data-testid={`kpi-block-${i}`}>
          <div className="text-xs font-semibold text-zinc-500">KPI {i + 1}</div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={k.value || ""} onChange={(e) => updateKpi(i, "value", e.target.value)} placeholder="Valore (es. +22%)" data-testid={`kpi-${i}-value`} />
            <Input value={k.delta || ""} onChange={(e) => updateKpi(i, "delta", e.target.value)} placeholder="Delta (es. +3pp)" data-testid={`kpi-${i}-delta`} />
            <Input value={k.label || ""} onChange={(e) => updateKpi(i, "label", e.target.value)} placeholder="Label" data-testid={`kpi-${i}-label`} />
            <Input value={k.delta_kind || "neutral"} onChange={(e) => updateKpi(i, "delta_kind", e.target.value)} placeholder="positive/neutral/warn" data-testid={`kpi-${i}-kind`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function PointsEditor({ data, onChange, max = 5, label = "Punti" }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const updatePt = (i, v) => {
    const points = [...(data.points || [])];
    points[i] = v;
    set("points", points);
  };
  const addPt = () => set("points", [...(data.points || []), ""]);
  const removePt = (i) => {
    const points = (data.points || []).filter((_, idx) => idx !== i);
    set("points", points);
  };
  return (
    <div className="grid gap-3">
      <FieldText label="Tag" value={data.tag} onChange={(v) => set("tag", v)} testid="pts-tag" />
      <FieldText label="Titolo" value={data.title} onChange={(v) => set("title", v)} testid="pts-title" />
      {data.disclaimer !== undefined && (
        <FieldText label="Disclaimer" value={data.disclaimer} onChange={(v) => set("disclaimer", v)} testid="pts-disclaimer" />
      )}
      <div className="text-xs font-semibold text-zinc-500">{label}</div>
      {(data.points || []).map((p, i) => (
        <div key={i} className="flex gap-2" data-testid={`pt-row-${i}`}>
          <span className="text-xs font-semibold mt-2 w-5">{i + 1}.</span>
          <Textarea value={p} onChange={(e) => updatePt(i, e.target.value)} className="flex-1 min-h-[60px]" data-testid={`pt-${i}`} />
          {(data.points || []).length > 1 && (
            <Button variant="ghost" size="sm" onClick={() => removePt(i)} data-testid={`pt-remove-${i}`}>×</Button>
          )}
        </div>
      ))}
      {(data.points || []).length < max && (
        <Button variant="outline" size="sm" onClick={addPt} className="self-start" data-testid="pt-add">+ Aggiungi punto</Button>
      )}
    </div>
  );
}

function QuoteEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3">
      <div>
        <Label>Citazione</Label>
        <Textarea value={data.citation} onChange={(e) => set("citation", e.target.value)} className="min-h-[100px]" data-testid="quote-text" />
      </div>
      <FieldText label="Autore" value={data.author} onChange={(v) => set("author", v)} testid="quote-author" />
      <FieldText label="Qualifica" value={data.qualifica} onChange={(v) => set("qualifica", v)} testid="quote-qual" />
    </div>
  );
}

function CtaEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <FieldText label="Titolo" value={data.title} onChange={(v) => set("title", v)} testid="cta-title" />
      <FieldText label="Sottotitolo" value={data.subtitle} onChange={(v) => set("subtitle", v)} testid="cta-subtitle" />
      <FieldText label="Telefono" value={data.phone} onChange={(v) => set("phone", v)} testid="cta-phone" />
      <FieldText label="IVASS tag" value={data.ivass_tag} onChange={(v) => set("ivass_tag", v)} testid="cta-ivass" />
      <FieldText label="Sedi" value={data.location} onChange={(v) => set("location", v)} testid="cta-location" />
    </div>
  );
}

// ============================================================================
// v1.14.53 — EDITORIAL editors (Geopop × Starting Finance style)
// ============================================================================
function EditorialBase({ data, onChange, showMetric = true, showKeywords = true, showBars = false }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const updateBar = (i, k, v) => {
    const bars = [...(data.bars || [])];
    bars[i] = { ...bars[i], [k]: k === "value" ? parseFloat(v) || 0 : v };
    set("bars", bars);
  };
  return (
    <div className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <FieldText label="Eyebrow (tag piccolo)" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="ed-eyebrow" placeholder="Es. IL PUNTO È QUESTO" />
        <FieldText label="Visual hint (icona)" value={data.visual_hint} onChange={(v) => set("visual_hint", v)} testid="ed-visual" placeholder="house / car / lightbulb / chart / mappa" />
      </div>
      <FieldText label="Headline (titolo grande)" value={data.headline} onChange={(v) => set("headline", v)} testid="ed-headline" />
      <div>
        <Label>Body (max 30-40 parole)</Label>
        <Textarea value={data.body || ""} onChange={(e) => set("body", e.target.value)} className="min-h-[80px]" data-testid="ed-body" />
      </div>
      {showKeywords && (
        <FieldText label="Keywords highlight (separate da virgola)"
          value={(data.keywords_highlight || []).join(", ")}
          onChange={(v) => set("keywords_highlight", v.split(",").map((x) => x.trim()).filter(Boolean))}
          testid="ed-kw" placeholder="Es. 140.000 furti, massima attenzione" />
      )}
      {showMetric && (
        <div className="grid grid-cols-3 gap-3">
          <FieldText label="Metrica hero" value={data.metric_main} onChange={(v) => set("metric_main", v)} testid="ed-metric" placeholder="+18% o 140.000" />
          <FieldText label="Unit" value={data.metric_unit} onChange={(v) => set("metric_unit", v)} testid="ed-metric-unit" />
          <FieldText label="Kind" value={data.metric_kind || "neutral"} onChange={(v) => set("metric_kind", v)} testid="ed-metric-kind" placeholder="negative/positive/neutral" />
        </div>
      )}
      {showBars && (
        <div>
          <Label>Mini bar chart (opzionale)</Label>
          {(data.bars || []).map((b, i) => (
            <div key={i} className="grid grid-cols-3 gap-2 mb-2" data-testid={`ed-bar-${i}`}>
              <Input value={b.label || ""} onChange={(e) => updateBar(i, "label", e.target.value)} placeholder="Label" />
              <Input value={b.value || ""} onChange={(e) => updateBar(i, "value", e.target.value)} placeholder="Value" type="number" />
              <Input value={b.value_display || ""} onChange={(e) => updateBar(i, "value_display", e.target.value)} placeholder="Display" />
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => set("bars", [...(data.bars || []), { label: "", value: 0 }])} data-testid="ed-bar-add">+ Riga</Button>
        </div>
      )}
      <FieldText label="Source label (fonte)" value={data.source_label} onChange={(v) => set("source_label", v)} testid="ed-source" placeholder="Es. ISTAT 2026" />
    </div>
  );
}

const CoverEditor = (props) => <EditorialBase {...props} showMetric showKeywords={false} />;
const ContextEditor = (props) => <EditorialBase {...props} showMetric={false} showKeywords />;
const EducationalEditor = (props) => <EditorialBase {...props} showMetric showKeywords showBars />;
const MappaEditor = ({ data, onChange }) => {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3">
      <EditorialBase data={data} onChange={onChange} showMetric={false} showKeywords />
      <FieldText label="Regioni (separate da virgola, max 8)"
        value={(data.mappa_regioni || []).join(", ")}
        onChange={(v) => set("mappa_regioni", v.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 8))}
        testid="ed-regioni" placeholder="Campania, Lombardia, Lazio" />
    </div>
  );
};
function SummaryV2Editor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const updatePt = (i, v) => {
    const points = [...(data.points || [])];
    points[i] = v;
    set("points", points);
  };
  return (
    <div className="grid gap-3">
      <FieldText label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="sumv2-eyebrow" />
      <FieldText label="Headline" value={data.headline} onChange={(v) => set("headline", v)} testid="sumv2-headline" />
      <Label>3 punti takeaway</Label>
      {(data.points || ["", "", ""]).slice(0, 3).map((p, i) => (
        <div key={i} className="flex gap-2" data-testid={`sumv2-pt-${i}`}>
          <span className="text-xs font-semibold mt-2 w-5">{i + 1}.</span>
          <Textarea value={p || ""} onChange={(e) => updatePt(i, e.target.value)} className="flex-1 min-h-[60px]" />
        </div>
      ))}
    </div>
  );
}
function CtaV2Editor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3">
      <FieldText label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="ctav2-eyebrow" />
      <FieldText label="Headline" value={data.headline} onChange={(v) => set("headline", v)} testid="ctav2-headline" />
      <div>
        <Label>Body (invito breve)</Label>
        <Textarea value={data.body || ""} onChange={(e) => set("body", e.target.value)} className="min-h-[80px]" data-testid="ctav2-body" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <FieldText label="Telefono" value={data.phone} onChange={(v) => set("phone", v)} testid="ctav2-phone" />
        <FieldText label="IVASS" value={data.source_label} onChange={(v) => set("source_label", v)} testid="ctav2-ivass" />
      </div>
    </div>
  );
}
function BigNumEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3">
      <FieldText label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="bn-eyebrow" />
      <div className="grid grid-cols-3 gap-3">
        <FieldText label="Metric (HUGE)" value={data.metric_main} onChange={(v) => set("metric_main", v)} testid="bn-metric" />
        <FieldText label="Unit" value={data.metric_unit} onChange={(v) => set("metric_unit", v)} testid="bn-unit" />
        <FieldText label="Kind" value={data.metric_kind} onChange={(v) => set("metric_kind", v)} testid="bn-kind" placeholder="negative/positive/neutral" />
      </div>
      <FieldText label="Sottotitolo (opzionale)" value={data.headline} onChange={(v) => set("headline", v)} testid="bn-headline" />
      <FieldText label="Source" value={data.source_label} onChange={(v) => set("source_label", v)} testid="bn-source" />
    </div>
  );
}
function QuoteCeoEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3">
      <FieldText label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="qc-eyebrow" />
      <div>
        <Label>Citazione (max 220 char)</Label>
        <Textarea value={data.quote_text || ""} onChange={(e) => set("quote_text", e.target.value)} className="min-h-[100px]" data-testid="qc-text" />
      </div>
      <FieldText label="Autore" value={data.quote_author} onChange={(v) => set("quote_author", v)} testid="qc-author" />
      <FieldText label="Ruolo" value={data.quote_role} onChange={(v) => set("quote_role", v)} testid="qc-role" />
    </div>
  );
}
function ComparisonEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const cmp = data.comparison || { left_title: "", left_points: [], right_title: "", right_points: [] };
  const setCmp = (k, v) => set("comparison", { ...cmp, [k]: v });
  return (
    <div className="grid gap-3">
      <FieldText label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} testid="cmp-eyebrow" />
      <FieldText label="Headline" value={data.headline} onChange={(v) => set("headline", v)} testid="cmp-headline" />
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <FieldText label="Colonna SINISTRA titolo" value={cmp.left_title} onChange={(v) => setCmp("left_title", v)} testid="cmp-left-t" />
          <Label className="mt-2">Punti (uno per riga)</Label>
          <Textarea value={(cmp.left_points || []).join("\n")} onChange={(e) => setCmp("left_points", e.target.value.split("\n"))} className="min-h-[100px]" data-testid="cmp-left-pts" />
        </div>
        <div>
          <FieldText label="Colonna DESTRA titolo" value={cmp.right_title} onChange={(v) => setCmp("right_title", v)} testid="cmp-right-t" />
          <Label className="mt-2">Punti (uno per riga)</Label>
          <Textarea value={(cmp.right_points || []).join("\n")} onChange={(e) => setCmp("right_points", e.target.value.split("\n"))} className="min-h-[100px]" data-testid="cmp-right-pts" />
        </div>
      </div>
    </div>
  );
}

function FieldText({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

// =====================================================================
// v1.14.55 — MEME EDITORS
// =====================================================================
function MemeBaseEditor({ data, onChange, options = {} }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid gap-3">
      <FieldText label="Counter text (es. 1/8)" value={data.counter_text} onChange={(v) => set("counter_text", v)} testid="meme-counter" />
      <div>
        <Label>Headline (max 90 char)</Label>
        <Textarea value={data.headline || ""} onChange={(e) => set("headline", e.target.value)} className="min-h-[80px]" data-testid="meme-headline" />
      </div>
      {options.showBody && (
        <div>
          <Label>Body (max 60 parole)</Label>
          <Textarea value={data.body || ""} onChange={(e) => set("body", e.target.value)} className="min-h-[80px]" data-testid="meme-body" />
        </div>
      )}
      {options.showMetric && (
        <div className="grid grid-cols-3 gap-2">
          <FieldText label="Metric main" value={data.metric_main} onChange={(v) => set("metric_main", v)} placeholder="138%" testid="meme-metric" />
          <FieldText label="Unit" value={data.metric_unit} onChange={(v) => set("metric_unit", v)} placeholder="vs 2024" testid="meme-unit" />
          <FieldText label="Kind" value={data.metric_kind} onChange={(v) => set("metric_kind", v)} placeholder="negative/positive/premium/neutral" testid="meme-mkind" />
        </div>
      )}
      {options.showAssets && (
        <FieldText label="Asset suggestions (csv: scudo,casa,...)" value={(data.asset_suggestions || []).join(",")} onChange={(v) => set("asset_suggestions", v.split(",").map((x) => x.trim()).filter(Boolean))} testid="meme-assets" />
      )}
      {options.children}
    </div>
  );
}
const MemePastEditor = (p) => <MemeBaseEditor {...p} options={{ showAssets: true }} />;
const MemePresentEditor = (p) => <MemeBaseEditor {...p} options={{ showAssets: true }} />;
function MemeNewsEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <MemeBaseEditor data={data} onChange={onChange} options={{
      children: (
        <div>
          <Label>Titoli News Card (uno per riga, FITTIZI — max 3)</Label>
          <Textarea value={(data.news_card_titles || []).join("\n")} onChange={(e) => set("news_card_titles", e.target.value.split("\n").slice(0, 3))} className="min-h-[80px]" data-testid="meme-news-titles" />
        </div>
      ),
    }} />
  );
}
const MemeBigNumberEditor = (p) => <MemeBaseEditor {...p} options={{ showBody: true, showMetric: true, showAssets: true }} />;
function MemeReasonsEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const blocks = data.reasons_blocks || [];
  const updateBlock = (i, k, v) => {
    const nb = [...blocks];
    nb[i] = { ...nb[i], [k]: v };
    set("reasons_blocks", nb);
  };
  return (
    <MemeBaseEditor data={data} onChange={onChange} options={{
      children: (
        <div className="space-y-2">
          <Label>Reasons blocks (2-4)</Label>
          {blocks.map((b, i) => (
            <div key={i} className="grid grid-cols-3 gap-2" data-testid={`meme-reason-${i}`}>
              <Input value={b.icon || ""} onChange={(e) => updateBlock(i, "icon", e.target.value)} placeholder="icon key (scudo, soldi, warning...)" />
              <Input className="col-span-2" value={b.text || ""} onChange={(e) => updateBlock(i, "text", e.target.value)} placeholder="testo blocco" />
            </div>
          ))}
        </div>
      ),
    }} />
  );
}
function MemeRisksEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const blocks = data.risks_blocks || [];
  const updateBlock = (i, k, v) => {
    const nb = [...blocks];
    nb[i] = { ...nb[i], [k]: v };
    set("risks_blocks", nb);
  };
  return (
    <MemeBaseEditor data={data} onChange={onChange} options={{
      children: (
        <div className="space-y-2">
          <Label>Risk blocks (2-3)</Label>
          {blocks.map((b, i) => (
            <div key={i} className="grid grid-cols-4 gap-2" data-testid={`meme-risk-${i}`}>
              <Input value={b.icon || ""} onChange={(e) => updateBlock(i, "icon", e.target.value)} placeholder="icon" />
              <Input value={b.title || ""} onChange={(e) => updateBlock(i, "title", e.target.value)} placeholder="title" />
              <Input className="col-span-2" value={b.subtitle || ""} onChange={(e) => updateBlock(i, "subtitle", e.target.value)} placeholder="subtitle" />
            </div>
          ))}
        </div>
      ),
    }} />
  );
}
function MemeImpactEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <MemeBaseEditor data={data} onChange={onChange} options={{
      showBody: true, showAssets: true,
      children: (
        <div className="grid grid-cols-3 gap-2">
          <FieldText label="Eyebrow InfoBox" value={data.info_box_eyebrow} onChange={(v) => set("info_box_eyebrow", v)} testid="meme-impact-eb" />
          <FieldText label="Titolo InfoBox" value={data.info_box_title} onChange={(v) => set("info_box_title", v)} testid="meme-impact-title" />
          <FieldText label="Bordo (purple/teal)" value={data.info_box_border} onChange={(v) => set("info_box_border", v)} testid="meme-impact-border" />
        </div>
      ),
    }} />
  );
}
function MemeCtaEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const cta = data.cta || {};
  const setCta = (k, v) => set("cta", { ...cta, [k]: v });
  return (
    <MemeBaseEditor data={data} onChange={onChange} options={{
      showAssets: true,
      children: (
        <div className="grid grid-cols-3 gap-2">
          <FieldText label="CTA text" value={cta.text} onChange={(v) => setCta("text", v)} testid="meme-cta-text" />
          <FieldText label="Thumb label" value={cta.thumb_label} onChange={(v) => setCta("thumb_label", v)} testid="meme-cta-thumb" />
          <FieldText label="Type" value={cta.type} onChange={(v) => setCta("type", v)} placeholder="podcast/report/consulenza/..." testid="meme-cta-type" />
        </div>
      ),
    }} />
  );
}

const EDITORS = {
  // v1.14.55 — meme editorial
  meme_past: MemePastEditor,
  meme_present: MemePresentEditor,
  meme_news: MemeNewsEditor,
  meme_bignumber: MemeBigNumberEditor,
  meme_reasons: MemeReasonsEditor,
  meme_risks: MemeRisksEditor,
  meme_impact: MemeImpactEditor,
  meme_cta: MemeCtaEditor,
  // v1.14.53 editorial
  cover: CoverEditor,
  context: ContextEditor,
  educational: EducationalEditor,
  summary: SummaryV2Editor,
  cta_v2: CtaV2Editor,
  bignum_hero: BigNumEditor,
  quote_ceo: QuoteCeoEditor,
  comparison: ComparisonEditor,
  mappa: MappaEditor,
  // legacy v1.14.51
  hook: HookEditor, barchart: BarchartEditor, kpi3: Kpi3Editor,
  warning: (props) => <PointsEditor {...props} max={5} label="Punti (warning, 1-5)" />,
  checklist: (props) => <PointsEditor {...props} max={7} label="Checklist (4-7 punti)" />,
  quote: QuoteEditor, cta: CtaEditor,
};

// =====================================================================
// MAIN PAGE
// =====================================================================
export default function AdminSocialGeneratorPage() {
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState(null);
  const [topic, setTopic] = useState("");
  const [topicCustom, setTopicCustom] = useState("");
  const [selectedSlideTypes, setSelectedSlideTypes] = useState([
    "cover", "context", "educational", "educational", "educational",
    "summary", "cta_v2",
  ]);
  const [draftId, setDraftId] = useState(null);
  const [slides, setSlides] = useState([]);
  const [caption, setCaption] = useState("");
  const [reelScript, setReelScript] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [aiProvider, setAiProvider] = useState("");
  const [verifPending, setVerifPending] = useState(false);
  const [accentColor, setAccentColor] = useState("#1B3A2D");
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [exports, setExports] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // v1.14.52
  const [freshnessMode, setFreshnessMode] = useState("auto");
  const [freshnessUsed, setFreshnessUsed] = useState(null);
  const [freshnessWarning, setFreshnessWarning] = useState(false);
  const [datiAggiornatiAl, setDatiAggiornatiAl] = useState("n.d.");
  const [sourcesFoundCount, setSourcesFoundCount] = useState(0);
  const [logos, setLogos] = useState([]);
  const [showLogoLibrary, setShowLogoLibrary] = useState(false);
  const [brandLogoId, setBrandLogoId] = useState(null);
  const [cobrandingEnabled, setCobrandingEnabled] = useState(false);
  const [partnerLogoId, setPartnerLogoId] = useState(null);
  // v1.14.54 — Illustrations AI
  const [illustrationsEnabled, setIllustrationsEnabled] = useState(true);
  const [slideIllustrations, setSlideIllustrations] = useState({}); // {idx: {illustration_id, preview_url}}
  const [generatingIllu, setGeneratingIllu] = useState(null); // idx in progress
  const [batchIlluLoading, setBatchIlluLoading] = useState(false);
  // v1.14.55 — Insurance Editorial Meme preset
  const [presetId, setPresetId] = useState("editorial");
  const [memeAudience, setMemeAudience] = useState("clienti");
  const [memeBranch, setMemeBranch] = useState("auto");
  const [memeTone, setMemeTone] = useState("educational");
  const [memeSlidesCount, setMemeSlidesCount] = useState(8);
  const [memeCtaType, setMemeCtaType] = useState("podcast");
  const [memeCtaText, setMemeCtaText] = useState("");
  const [memeBrandName, setMemeBrandName] = useState("Colucci Assicuratori");
  // v1.14.56 — Quality tier + AI status
  const [qualityTier, setQualityTier] = useState("standard"); // overridden by preset default in effect below
  const [aiStatus, setAiStatus] = useState(null);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [draftFromCache, setDraftFromCache] = useState(false);
  const [aiProviderLabel, setAiProviderLabel] = useState("");
  const [aiWasDowngraded, setAiWasDowngraded] = useState(false);
  const [aiLatencyMs, setAiLatencyMs] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/social/meta`, axc)
      .then((r) => {
        setMeta(r.data);
        if (r.data.default_accent) setAccentColor(r.data.default_accent);
      })
      .catch((e) => {
        if (e.response?.status === 401) toast.error("Sessione scaduta. Effettua il login.");
        else toast.error(`Errore caricamento meta: ${e.message}`);
      });
    // v1.14.56 — fetch AI status (probe claude-fable-5)
    axios.get(`${API}/admin/social/ai-status`, axc)
      .then((r) => setAiStatus(r.data))
      .catch(() => { /* non-blocking */ });
    refreshExports();
    refreshLogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v1.14.56 — Auto-set quality_tier in base al preset (override-abile)
  useEffect(() => {
    if (presetId === "insurance_editorial_meme") setQualityTier("premium");
    else setQualityTier("standard");
  }, [presetId]);

  const refreshLogos = () => {
    axios.get(`${API}/admin/social/logos`, axc)
      .then((r) => {
        const items = r.data.items || [];
        setLogos(items);
        // Auto-select default brand if not set yet
        if (!brandLogoId) {
          const def = items.find((l) => l.is_default_brand);
          if (def) setBrandLogoId(def.id);
        }
      })
      .catch(() => {});
  };

  const refreshExports = () => {
    axios.get(`${API}/admin/social/exports?limit=30`, axc)
      .then((r) => setExports(r.data.items || []))
      .catch(() => {});
  };

  const finalTopic = topic === "__custom__" ? topicCustom : topic;
  const canNext1 = !!finalTopic && finalTopic.length >= 3;
  const canNext2 = presetId === "insurance_editorial_meme"
    ? true
    : (selectedSlideTypes.length >= 2 && selectedSlideTypes.length <= 10);
  const canExport = slides.length > 0 && !loadingExport;

  const requestDraft = async () => {
    if (!finalTopic) return;
    setLoadingDraft(true);
    try {
      const payload = {
        topic: finalTopic,
        slide_types: selectedSlideTypes,
        freshness_mode: freshnessMode,
        preset_id: presetId,
        // v1.14.56 — quality tier + force refresh
        quality_tier: qualityTier,
        force_refresh: forceRefresh,
      };
      if (presetId === "insurance_editorial_meme") {
        payload.audience = memeAudience;
        payload.insurance_branch = memeBranch;
        payload.tone = memeTone;
        payload.brand_name = memeBrandName;
        payload.slides_count = memeSlidesCount;
        payload.cta_type = memeCtaType;
        payload.cta_text = memeCtaText;
      }
      const { data } = await axios.post(`${API}/admin/social/draft`, payload, axc);
      setDraftId(data.draft_id);
      setSlides(data.slides || []);
      setCaption(data.caption_instagram || "");
      setReelScript(data.reel_script || "");
      setHashtags(data.suggested_hashtags || []);
      setAiProvider(data.ai_provider_used || "");
      setVerifPending(!!data.verifica_manuale_pending);
      setFreshnessUsed(data.freshness_mode_used || null);
      setFreshnessWarning(!!data.freshness_warning);
      setDatiAggiornatiAl(data.dati_aggiornati_al || "n.d.");
      setSourcesFoundCount(data.sources_found_count || 0);
      // v1.14.56 — routing metadata
      setAiProviderLabel(data.ai_provider_label || "");
      setAiWasDowngraded(!!data.ai_was_downgraded);
      setAiLatencyMs(data.ai_latency_ms || 0);
      setDraftFromCache(!!data.draft_from_cache);
      setForceRefresh(false); // reset after use
      const tierLbl = qualityTier === "premium" ? "Premium" : "Standard";
      const cacheLbl = data.draft_from_cache ? " (CACHE)" : "";
      toast.success(`Draft generato — ${tierLbl}${cacheLbl} · ${data.ai_provider_label || data.ai_provider_used}`);
      setStep(2);
    } catch (e) {
      toast.error(`AI draft fallito: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoadingDraft(false);
    }
  };

  const exportZip = async () => {
    setLoadingExport(true);
    try {
      const { data } = await axios.post(`${API}/admin/social/export`, {
        topic: finalTopic,
        slides: slides.map((s, i) => ({
          type: s.type,
          data: s.data,
          // v1.14.54 — passa illustration_id se generata
          illustration_id: slideIllustrations[i]?.illustration_id || null,
        })),
        branding: {
          accent_color: accentColor,
          brand_logo_id: brandLogoId,
          cobranding: {
            enabled: cobrandingEnabled,
            partner_logo_id: partnerLogoId,
            position: "header_dual",
          },
        },
        caption_instagram: caption,
        reel_script: reelScript,
        suggested_hashtags: hashtags,
        draft_id: draftId,
      }, axc);
      toast.success(`Export OK: ${data.zip_filename} (${(data.zip_size_bytes / 1024).toFixed(0)} KB)`);
      const a = document.createElement("a");
      a.href = `${API}/admin/social/exports/${data.export_id}/download`;
      a.download = data.zip_filename;
      a.click();
      refreshExports();
    } catch (e) {
      toast.error(`Export fallito: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoadingExport(false);
    }
  };

  const uploadLogo = async (file, label, isDefault) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label);
    fd.append("is_default_brand", isDefault ? "true" : "false");
    try {
      await axios.post(`${API}/admin/social/logos/upload`, fd, {
        ...axc, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo caricato");
      refreshLogos();
    } catch (e) {
      toast.error(`Upload fallito: ${e.response?.data?.detail || e.message}`);
    }
  };

  const deleteLogo = async (id) => {
    if (!confirm("Eliminare questo logo?")) return;
    try {
      await axios.delete(`${API}/admin/social/logos/${id}`, axc);
      toast.success("Logo eliminato");
      refreshLogos();
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    }
  };

  const setLogoDefault = async (id) => {
    try {
      await axios.patch(`${API}/admin/social/logos/${id}/set-default`, {}, axc);
      toast.success("Default brand aggiornato");
      refreshLogos();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    }
  };

  // v1.14.54 — Illustrations AI helpers
  const generateIllustrationForSlide = async (idx) => {
    const slide = slides[idx];
    if (!slide) return;
    const hint = (slide.data || {}).visual_hint;
    if (!hint) {
      toast.error("Manca 'visual_hint' per questa slide");
      return;
    }
    setGeneratingIllu(idx);
    try {
      const { data } = await axios.post(`${API}/admin/social/illustrate/single`, {
        topic: finalTopic, visual_hint: hint, use_cache: true,
      }, axc);
      if (!data.illustration_id) {
        toast.error(`Generazione fallita: ${data.error || "errore sconosciuto"}`);
        return;
      }
      setSlideIllustrations((prev) => ({ ...prev, [idx]: data }));
      toast.success(`Illustrazione ${data.from_cache ? "(cache)" : "generata"} OK`);
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    } finally {
      setGeneratingIllu(null);
    }
  };

  const removeIllustrationForSlide = (idx) => {
    setSlideIllustrations((prev) => {
      const next = { ...prev }; delete next[idx]; return next;
    });
  };

  const generateAllIllustrations = async () => {
    const requests = slides
      .map((s, i) => ({ slide_index: i, visual_hint: (s.data || {}).visual_hint }))
      .filter((r) => r.visual_hint);
    if (requests.length === 0) {
      toast.error("Nessuna slide ha 'visual_hint' valorizzato");
      return;
    }
    setBatchIlluLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/social/illustrate`, {
        topic: finalTopic, slides: requests, use_cache: true,
      }, axc);
      const next = { ...slideIllustrations };
      for (const i of data.illustrations) {
        if (i.illustration_id) next[i.slide_index] = i;
      }
      setSlideIllustrations(next);
      toast.success(`Illustrazioni: ${Object.keys(next).length}/${slides.length} · costo $${data.total_cost_usd_est}`);
    } catch (e) {
      toast.error(`Errore: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBatchIlluLoading(false);
    }
  };

  const updateSlideData = (i, newData) => {
    const next = [...slides];
    next[i] = { ...next[i], data: newData };
    setSlides(next);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text || "");
    toast.success(`${label} copiato`);
  };

  const deleteExport = async (id) => {
    if (!confirm("Sicuro di voler eliminare questo export?")) return;
    try {
      await axios.delete(`${API}/admin/social/exports/${id}`, axc);
      toast.success("Eliminato");
      refreshExports();
    } catch (e) {
      toast.error(`Errore: ${e.message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="social-generator-page">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-zinc-900">Social Carousel Generator</h2>
          <p className="text-sm text-zinc-500 mt-1">
            AI draft (GPT-5.4 + Gemini fallback) → edit manuale → export ZIP carosello Instagram 1080×1350 brandizzato Colucci.
          </p>
        </div>
        <div className="flex gap-2 items-start">
          <Button variant="outline" onClick={() => setShowLogoLibrary(!showLogoLibrary)} data-testid="toggle-logos">
            <ImageIcon className="w-4 h-4 mr-2" />
            Loghi ({logos.length})
          </Button>
          <Button variant="outline" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history">
            <History className="w-4 h-4 mr-2" />
            {showHistory ? "Nascondi" : "Storico"} ({exports.length})
          </Button>
        </div>
      </div>

      {showLogoLibrary && (
        <Card data-testid="logo-library">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Libreria Loghi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="border-dashed border-2 border-zinc-200 rounded p-3 text-sm">
                <Label>Upload nuovo logo (PNG/SVG, max 2MB)</Label>
                <Input id="logo-file" type="file" accept="image/png,image/svg+xml,image/webp,image/jpeg" data-testid="logo-file-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const label = prompt("Etichetta logo (es. 'Colucci Assicuratori'):");
                    if (!label) return;
                    const isDefault = confirm("Impostare come BRAND DEFAULT? (sì = Colucci ufficiale, no = secondario/partner)");
                    uploadLogo(f, label, isDefault);
                    e.target.value = "";
                  }} />
              </div>
              <div className="text-xs text-zinc-500 self-center">
                Il logo PNG/SVG transparent viene incorporato come data-URL base64 nei PNG del carosello (1080×1350). Per il co-branding il logo Colucci appare alto-sx, il partner accanto, con divisore "×".
              </div>
            </div>
            {logos.length === 0 ? (
              <div className="text-sm text-zinc-500" data-testid="no-logos">Nessun logo in libreria. Usa il default cerchio "C" verde scuro (CSS).</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="logos-grid">
                {logos.map((l) => (
                  <div key={l.id} className={`border rounded-lg p-3 space-y-2 ${l.is_default_brand ? "border-emerald-400 bg-emerald-50" : "border-zinc-200"}`} data-testid={`logo-${l.id}`}>
                    <img src={`${API}/admin/social/logos/${l.id}/preview`} alt={l.label} className="w-full h-24 object-contain bg-zinc-100 rounded" />
                    <div className="text-xs font-semibold truncate">{l.label}</div>
                    <div className="text-[10px] text-zinc-500">{l.width}×{l.height} {l.format} {l.transparent_bg ? "α" : ""}</div>
                    {l.is_default_brand && <div className="text-[10px] text-emerald-700 font-semibold inline-flex items-center gap-1"><Star className="w-3 h-3" /> DEFAULT</div>}
                    <div className="flex gap-1">
                      {!l.is_default_brand && (
                        <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2" onClick={() => setLogoDefault(l.id)} data-testid={`logo-set-default-${l.id}`}>
                          Set default
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-red-600" onClick={() => deleteLogo(l.id)} data-testid={`logo-delete-${l.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showHistory && (
        <Card data-testid="exports-history">
          <CardHeader><CardTitle className="text-base">Storico Export</CardTitle></CardHeader>
          <CardContent>
            {exports.length === 0 ? <div className="text-sm text-zinc-500">Nessun export ancora.</div> : (
              <div className="space-y-2">
                {exports.map((e) => (
                  <div key={e.id} className="flex items-center justify-between border rounded p-2 text-sm" data-testid={`exp-row-${e.id}`}>
                    <div>
                      <div className="font-medium">{e.topic}</div>
                      <div className="text-xs text-zinc-500">
                        {e.status} · {e.slide_count} slides · {(e.zip_size_bytes / 1024).toFixed(0)} KB · {e.created_at?.slice(0, 16).replace("T", " ")}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {e.zip_path && (
                        <a href={`${API}/admin/social/exports/${e.id}/download`} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="outline" data-testid={`exp-dl-${e.id}`}>
                            <Download className="w-3 h-3" />
                          </Button>
                        </a>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteExport(e.id)} data-testid={`exp-del-${e.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stepper */}
      <div className="flex flex-wrap gap-2 items-center">
        {STEP_LABELS.map((l, i) => (
          <StepBadge key={l} index={i} label={l} active={step === i} done={step > i} />
        ))}
      </div>

      {/* STEP 1: Topic */}
      {step === 0 && (
        <Card data-testid="step-1">
          <CardHeader><CardTitle className="text-base">1. Scegli il preset + topic</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {/* v1.14.55 — Preset selector */}
            <div className="bg-zinc-50 rounded-lg p-3 space-y-2" data-testid="preset-selector">
              <Label className="text-sm font-semibold">Preset</Label>
              <select className="w-full border rounded p-2 text-sm bg-white" value={presetId}
                onChange={(e) => {
                  const np = e.target.value;
                  setPresetId(np);
                  if (np === "insurance_editorial_meme") {
                    setSelectedSlideTypes(MEME_TEMPLATE_ORDER.slice(0, memeSlidesCount));
                  } else {
                    setSelectedSlideTypes([
                      "cover", "context", "educational", "educational", "educational",
                      "summary", "cta_v2",
                    ]);
                  }
                }}
                data-testid="preset-dropdown">
                {PRESET_OPTIONS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              <div className="text-xs text-zinc-500">
                {PRESET_OPTIONS.find((p) => p.id === presetId)?.desc}
              </div>
            </div>

            {/* v1.14.55 — Meme extra fields */}
            {presetId === "insurance_editorial_meme" && (
              <div className="border-2 border-emerald-200 bg-emerald-50/40 rounded-lg p-3 space-y-3" data-testid="meme-extra-fields">
                <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Insurance Meme — Configurazione</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Audience</Label>
                    <select className="w-full border rounded p-2 text-sm bg-white" value={memeAudience}
                      onChange={(e) => setMemeAudience(e.target.value)} data-testid="meme-audience">
                      {MEME_AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Insurance Branch</Label>
                    <select className="w-full border rounded p-2 text-sm bg-white" value={memeBranch}
                      onChange={(e) => setMemeBranch(e.target.value)} data-testid="meme-branch">
                      {MEME_BRANCHES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Tone</Label>
                    <select className="w-full border rounded p-2 text-sm bg-white" value={memeTone}
                      onChange={(e) => setMemeTone(e.target.value)} data-testid="meme-tone">
                      {MEME_TONES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Slides count (5-10)</Label>
                    <Input type="number" min={5} max={10} value={memeSlidesCount}
                      onChange={(e) => {
                        const n = Math.max(5, Math.min(10, parseInt(e.target.value || "8", 10)));
                        setMemeSlidesCount(n);
                        setSelectedSlideTypes(MEME_TEMPLATE_ORDER.slice(0, n));
                      }} data-testid="meme-slides-count" />
                  </div>
                  <div>
                    <Label className="text-xs">CTA Type</Label>
                    <select className="w-full border rounded p-2 text-sm bg-white" value={memeCtaType}
                      onChange={(e) => setMemeCtaType(e.target.value)} data-testid="meme-cta-type-sel">
                      {MEME_CTA_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">CTA Text (opzionale)</Label>
                    <Input value={memeCtaText} onChange={(e) => setMemeCtaText(e.target.value)} placeholder="Es. Ascolta l'episodio" data-testid="meme-cta-text-in" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Brand name</Label>
                  <Input value={memeBrandName} onChange={(e) => setMemeBrandName(e.target.value)} data-testid="meme-brand-name" />
                </div>
              </div>
            )}

            {/* v1.14.56 — Quality Tier Premium/Standard */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3 space-y-2" data-testid="quality-tier-box">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-semibold">Qualità AI</Label>
                {aiStatus?.fable5_available && (
                  <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">FABLE 5 ATTIVO</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setQualityTier("premium")}
                  className={`text-left rounded-lg p-3 border-2 transition ${qualityTier === "premium" ? "border-indigo-500 bg-white shadow-sm" : "border-zinc-200 bg-white/50"}`}
                  data-testid="tier-premium-btn">
                  <div className="text-xs font-bold tracking-wider text-indigo-700">PREMIUM ⚡</div>
                  <div className="text-sm font-semibold mt-0.5">
                    {aiStatus?.active_premium_label || "Claude Fable 5"}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    ~${aiStatus?.active_premium_cost_per_draft_usd?.toFixed(3) || "0.165"} / draft
                    {aiStatus?.fable5_available === false && (
                      <span className="block text-amber-600 mt-0.5">⚠ Fable 5 non ancora abilitato — usa Opus 4.8</span>
                    )}
                  </div>
                </button>
                <button type="button" onClick={() => setQualityTier("standard")}
                  className={`text-left rounded-lg p-3 border-2 transition ${qualityTier === "standard" ? "border-zinc-500 bg-white shadow-sm" : "border-zinc-200 bg-white/50"}`}
                  data-testid="tier-standard-btn">
                  <div className="text-xs font-bold tracking-wider text-zinc-700">STANDARD</div>
                  <div className="text-sm font-semibold mt-0.5">GPT-5</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    ~${aiStatus?.standard_cost_per_draft_usd?.toFixed(3) || "0.080"} / draft
                  </div>
                </button>
              </div>
              <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer mt-1" data-testid="force-refresh-label">
                <input type="checkbox" checked={forceRefresh} onChange={(e) => setForceRefresh(e.target.checked)} data-testid="force-refresh-checkbox" />
                <span>Forza rigenerazione (ignora cache 24h)</span>
              </label>
            </div>

            <div className="space-y-2">
              {(meta?.topics || []).map((t) => (
                <label key={t.key} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50 ${topic === t.label ? "bg-emerald-50 border-emerald-300" : ""}`} data-testid={`topic-${t.key}`}>
                  <input type="radio" name="topic" checked={topic === t.label} onChange={() => { setTopic(t.label); setSelectedSlideTypes(t.suggested_slide_types || selectedSlideTypes); }} className="mt-1" />
                  <div>
                    <div className="font-medium text-sm">{t.label}</div>
                    <div className="text-xs text-zinc-500">{t.suggested_slide_types?.length || 0} slide suggerite</div>
                  </div>
                </label>
              ))}
              <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50 ${topic === "__custom__" ? "bg-emerald-50 border-emerald-300" : ""}`} data-testid="topic-custom">
                <input type="radio" name="topic" checked={topic === "__custom__"} onChange={() => setTopic("__custom__")} className="mt-1" />
                <div className="flex-1">
                  <div className="font-medium text-sm">Altro topic…</div>
                  {topic === "__custom__" && (
                    <Input className="mt-2" value={topicCustom} onChange={(e) => setTopicCustom(e.target.value)} placeholder="Es. Polizza viaggio per vacanze invernali" data-testid="topic-custom-input" />
                  )}
                </div>
              </label>
            </div>
            <div className="flex justify-end pt-2">
              <Button disabled={!canNext1} onClick={() => setStep(1)} data-testid="next-1">Avanti →</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Slide types */}
      {step === 1 && (
        <Card data-testid="step-2">
          <CardHeader><CardTitle className="text-base">2. Scegli mix slide (min 2, max 10) + Freshness AI</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {presetId === "insurance_editorial_meme" && (
              <div className="bg-emerald-50 border border-emerald-300 rounded p-3 text-sm" data-testid="meme-locked-info">
                <div className="font-bold text-emerald-900">Insurance Meme — narrativa fissa</div>
                <div className="text-xs text-emerald-800 mt-1">
                  Il preset usa una narrativa di {memeSlidesCount} slide (Past → Present → News → BigNumber → Reasons → Risks → Impact → CTA). Il mix è auto-determinato. Puoi solo cambiare audience/branch/tone/CTA nello Step 1.
                </div>
              </div>
            )}
            <div className="bg-zinc-50 rounded p-3 space-y-2" data-testid="freshness-selector">
              <Label>Freshness Mode</Label>
              <div className="grid sm:grid-cols-3 gap-2">
                {FRESHNESS_MODES.map((m) => {
                  const Icon = m.icon;
                  const active = freshnessMode === m.value;
                  return (
                    <label key={m.value} className={`border rounded-lg p-2 cursor-pointer hover:bg-zinc-100 ${active ? "border-emerald-400 bg-emerald-50" : "border-zinc-200"}`} data-testid={`fresh-mode-${m.value}`}>
                      <input type="radio" name="fresh" checked={active} onChange={() => setFreshnessMode(m.value)} className="mr-2" />
                      <Icon className="w-3 h-3 inline mr-1" />
                      <span className="font-semibold text-xs">{m.label}</span>
                      <div className="text-[10px] text-zinc-500 mt-1">{m.desc}</div>
                    </label>
                  );
                })}
              </div>
              <div className="text-[10px] text-zinc-500">
                HARD esegue una research call separata (Gemini 2.5 Flash) per ottenere fonti recenti italiane, poi le passa come context al draft. Cache 24h per topic.
              </div>
            </div>
            {/* Slide types raggruppati per categoria */}
            {["principale", "avanzato", "legacy"].map((cat) => {
              const items = (meta?.slide_types || []).filter((s) => (s.category || "principale") === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat} className="space-y-2" data-testid={`slide-cat-${cat}`}>
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 pt-2">
                    {cat === "principale" ? "Editorial principali (v1.14.53)" : cat === "avanzato" ? "Variant avanzati" : "Legacy v1.14.51 (deprecati)"}
                  </div>
                  {items.map((s) => (
                    <label key={s.type} className={`flex items-start gap-3 p-2 border rounded cursor-pointer hover:bg-zinc-50 ${cat === "legacy" ? "opacity-60" : ""}`} data-testid={`slide-type-${s.type}`}>
                      <input type="checkbox" checked={selectedSlideTypes.includes(s.type)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedSlideTypes([...selectedSlideTypes, s.type]);
                          else setSelectedSlideTypes(selectedSlideTypes.filter((x) => x !== s.type));
                        }}
                        className="mt-1" />
                      <div>
                        <div className="font-medium text-sm">{s.label}</div>
                        <div className="text-xs text-zinc-500">{s.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              );
            })}
            {selectedSlideTypes.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm space-y-2" data-testid="slide-order">
                <div className="font-semibold text-xs uppercase tracking-wider text-emerald-800">Ordine slide selezionate (riordina con ↑↓)</div>
                {selectedSlideTypes.map((t, i) => (
                  <div key={`${t}-${i}`} className="flex items-center gap-2 bg-white border rounded px-2 py-1 text-xs">
                    <span className="font-mono text-zinc-500 w-6">{i + 1}.</span>
                    <span className="flex-1 font-medium">{t}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={i === 0}
                      onClick={() => {
                        const next = [...selectedSlideTypes];
                        [next[i - 1], next[i]] = [next[i], next[i - 1]];
                        setSelectedSlideTypes(next);
                      }} data-testid={`up-${i}`}>↑</Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" disabled={i === selectedSlideTypes.length - 1}
                      onClick={() => {
                        const next = [...selectedSlideTypes];
                        [next[i + 1], next[i]] = [next[i], next[i + 1]];
                        setSelectedSlideTypes(next);
                      }} data-testid={`down-${i}`}>↓</Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600"
                      onClick={() => setSelectedSlideTypes(selectedSlideTypes.filter((_, idx) => idx !== i))} data-testid={`rm-${i}`}>×</Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between pt-3">
              <Button variant="outline" onClick={() => setStep(0)} data-testid="back-2">← Indietro</Button>
              <Button disabled={!canNext2 || loadingDraft} onClick={requestDraft} data-testid="ai-draft-btn">
                {loadingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Genera AI Draft ({freshnessMode.toUpperCase()})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3: AI draft + edit */}
      {step === 2 && (
        <div className="space-y-4" data-testid="step-3">
          <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap" data-testid="ai-meta-row">
            <Sparkles className="w-3 h-3 text-emerald-600" />
            <span>AI: <code>{aiProviderLabel || aiProvider}</code>{aiLatencyMs > 0 && ` · ${(aiLatencyMs / 1000).toFixed(1)}s`}</span>
            {aiWasDowngraded && (
              <span className="inline-flex items-center gap-1 text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded" data-testid="downgrade-badge">
                ⚙ downgrade automatico (Fable 5 non ancora attivo sul proxy)
              </span>
            )}
            {draftFromCache && (
              <span className="inline-flex items-center gap-1 text-zinc-700 bg-zinc-100 border border-zinc-300 px-2 py-0.5 rounded" data-testid="cache-badge">
                ♻ da cache 24h
              </span>
            )}
            {freshnessUsed && (
              <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                <Globe className="w-3 h-3" /> freshness=<b>{freshnessUsed}</b>
                {sourcesFoundCount > 0 && ` · ${sourcesFoundCount} fonti`}
                {datiAggiornatiAl !== "n.d." && ` · dati ${datiAggiornatiAl}`}
              </span>
            )}
            {freshnessWarning && (
              <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                <AlertTriangle className="w-3 h-3" /> Freshness warning: alcuni dati potrebbero essere obsoleti
              </span>
            )}
            {verifPending && (
              <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                <AlertTriangle className="w-3 h-3" /> Verifica manuale richiesta su alcune slide
              </span>
            )}
          </div>
          {slides.map((s, i) => {
            const Editor = EDITORS[s.type] || (() => <div>Editor mancante per {s.type}</div>);
            const illu = slideIllustrations[i];
            const hasHint = !!(s.data || {}).visual_hint;
            return (
              <Card key={i} data-testid={`slide-card-${i}`}>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
                    <span className="bg-zinc-100 px-2 py-0.5 rounded text-xs font-mono">{i + 1}/{slides.length}</span>
                    <span className="capitalize">{s.type}</span>
                    {s.verifica_manuale && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                    {s.fonti_verificate?.length > 0 && (
                      <span className="text-xs text-zinc-500">Fonti: {s.fonti_verificate.join(", ")}</span>
                    )}
                    {illu && (
                      <span className="ml-auto inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-xs">
                        <ImageIcon className="w-3 h-3" /> Illu AI {illu.from_cache ? "(cache)" : "✓"}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Editor data={s.data || SLIDE_PLACEHOLDER[s.type] || {}} onChange={(nd) => updateSlideData(i, nd)} />
                  {/* v1.14.54 — illustration controls */}
                  <div className="mt-3 pt-3 border-t flex items-center gap-3 flex-wrap" data-testid={`illu-controls-${i}`}>
                    {illu?.preview_url ? (
                      <>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${illu.preview_url}`} alt="illu"
                          className="w-32 h-32 object-contain bg-zinc-100 rounded border" />
                        <div className="text-xs text-zinc-500 flex-1">
                          <div className="font-mono">{illu.illustration_id?.slice(0, 8)}</div>
                          <div>Costo: ${illu.cost_usd_est || 0}</div>
                          <div>Cache: {illu.from_cache ? "✓" : "no"}</div>
                        </div>
                        <Button size="sm" variant="outline" disabled={generatingIllu === i} onClick={() => generateIllustrationForSlide(i)} data-testid={`illu-regen-${i}`}>
                          {generatingIllu === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Rigenera
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeIllustrationForSlide(i)} data-testid={`illu-remove-${i}`}>
                          <Trash2 className="w-3 h-3" /> Rimuovi
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" disabled={!hasHint || generatingIllu === i}
                        onClick={() => generateIllustrationForSlide(i)} data-testid={`illu-gen-${i}`}>
                        {generatingIllu === i ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        Genera illustrazione AI {hasHint ? "" : "(visual_hint vuoto)"}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <Card data-testid="caption-card">
            <CardHeader><CardTitle className="text-sm">Caption Instagram</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} className="min-h-[180px] font-mono text-sm" data-testid="caption-text" />
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(caption, "Caption")} data-testid="copy-caption">
                <Copy className="w-3 h-3 mr-1" /> Copia Caption
              </Button>
            </CardContent>
          </Card>

          <Card data-testid="reel-card">
            <CardHeader><CardTitle className="text-sm">Copione Reel</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Textarea value={reelScript} onChange={(e) => setReelScript(e.target.value)} className="min-h-[180px] font-mono text-sm" data-testid="reel-text" />
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(reelScript, "Copione Reel")} data-testid="copy-reel">
                <Copy className="w-3 h-3 mr-1" /> Copia Copione
              </Button>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="back-3">← Indietro</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={requestDraft} disabled={loadingDraft} data-testid="redraft-btn">
                {loadingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Rigenera draft
              </Button>
              <Button onClick={() => setStep(3)} data-testid="next-3">Avanti →</Button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: branding + export */}
      {step === 3 && (
        <Card data-testid="step-4">
          <CardHeader><CardTitle className="text-base">4. Branding & Export ZIP</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Colore Accent</Label>
                <div className="flex gap-2 items-center">
                  <Input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="h-10 w-16" data-testid="accent-picker" />
                  <Input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} data-testid="accent-text" />
                </div>
                <div className="text-xs text-zinc-500 mt-1">Default: #1B3A2D (verde scuro Colucci)</div>
              </div>
              <div>
                <Label>Topic</Label>
                <Input value={finalTopic} readOnly data-testid="export-topic" />
              </div>
            </div>

            {/* v1.14.54 — Batch Illustrations AI */}
            <div className="border rounded-lg p-3 space-y-2" data-testid="illu-batch-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-sm font-semibold inline-flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Illustrazioni AI (nano-banana)
                </Label>
                <span className="text-xs text-zinc-500">
                  Generate: {Object.keys(slideIllustrations).length}/{slides.length} · costo extra stimato: ${(slides.filter((s, i) => !slideIllustrations[i] && (s.data || {}).visual_hint).length * 0.039).toFixed(3)}
                </span>
              </div>
              <Button size="sm" variant="outline" disabled={batchIlluLoading} onClick={generateAllIllustrations} data-testid="illu-batch-btn">
                {batchIlluLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Genera illustrazioni mancanti
              </Button>
              <div className="text-[10px] text-zinc-500">
                Coerenza visiva: tutte le illustrazioni usano lo stesso style prefix Colucci. Cache 24h per topic+visual_hint.
              </div>
            </div>

            <div className="bg-zinc-50 rounded p-3 text-sm space-y-1">
              <div><strong>Slide totali:</strong> {slides.length}</div>
              <div><strong>Tipi:</strong> {slides.map((s) => s.type).join(" → ")}</div>
              <div><strong>Caption:</strong> {(caption || "").length} caratteri</div>
              <div><strong>Reel script:</strong> {(reelScript || "").length} caratteri</div>
              {verifPending && (
                <div className="text-amber-700 inline-flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Alcune slide hanno dati con verifica manuale richiesta.
                </div>
              )}
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="back-4">← Indietro</Button>
              <Button onClick={exportZip} disabled={!canExport} data-testid="export-zip-btn">
                {loadingExport ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
                Esporta ZIP ({slides.length} slide)
              </Button>
            </div>
            {!loadingExport && exports.length > 0 && exports[0].status === "exported" && (
              <div className="text-xs text-emerald-700 inline-flex items-center gap-1 pt-2">
                <CheckCircle2 className="w-3 h-3" /> Ultimo export: {exports[0].topic} ({(exports[0].zip_size_bytes / 1024).toFixed(0)} KB)
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
